import React, { useMemo, useState } from 'react';
import { CheckCircle2Icon, XCircleIcon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SignaturePad } from '../ui/SignaturePad';
interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
}
function buildQuizQuestions(data: any): QuizQuestion[] {
  const steps = data.stepHazardData || [];
  const questions: QuizQuestion[] = [];
  // Q1: General — what to do if conditions change
  questions.push({
    question: 'What should you do if conditions change during the task?',
    options: ['Continue working and report later', 'Stop work immediately and reassess', 'Speed up to finish before it gets worse', 'Wait for someone else to notice'],
    correctIndex: 1
  });
  // Q2: From first step hazard
  if (steps.length > 0 && steps[0].hazards?.length > 0) {
    const h = steps[0].hazards[0];
    questions.push({
      question: `What is the primary hazard during "${steps[0].description}"?`,
      options: ['Noise exposure', h.hazard, 'Slips and trips nearby', 'Chemical exposure'],
      correctIndex: 1
    });
  }
  // Q3: Controls question from a middle step
  const midIdx = Math.floor(steps.length / 2);
  if (steps.length > 1 && steps[midIdx]?.hazards?.length > 0) {
    const h = steps[midIdx].hazards[0];
    questions.push({
      question: `What control is required for "${steps[midIdx].description}"?`,
      options: ['No special controls needed', 'Only PPE is required', h.controls?.eliminateSubstitute || 'Follow elimination controls', 'Work faster to reduce exposure time'],
      correctIndex: 2
    });
  }
  // Q4: PPE question
  questions.push({
    question: 'What must every crew member verify before starting work?',
    options: ['That the schedule is on time', 'That their phone is charged', 'That required PPE is worn and in good condition', 'That they have eaten lunch'],
    correctIndex: 2
  });
  // Q5: Emergency response
  questions.push({
    question: 'What is the first action if an emergency occurs on site?',
    options: ['Continue working until told to stop', 'Call the office to report', 'Stop work, alert others, and follow emergency procedures', 'Take a photo for documentation'],
    correctIndex: 2
  });
  return questions.slice(0, 5);
}
export function Screen19_ForemanQuiz({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const QUIZ_QUESTIONS = useMemo(() => buildQuizQuestions(data), [data]);
  const [currentQ, setCurrentQ] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [quizAcknowledgmentSignature, setQuizAcknowledgmentSignature] = useState<string | null>(null);
  const question = QUIZ_QUESTIONS[currentQ];
  const isCorrect = selectedOption === question?.correctIndex;
  const handleSelect = (index: number) => {
    if (answered) return;
    setSelectedOption(index);
  };
  const handleConfirm = () => {
    if (selectedOption === null) return;
    if (!answered) {
      setAnswered(true);
      if (selectedOption === question.correctIndex) {
        setScore(s => s + 1);
      }
    } else {
      if (currentQ < QUIZ_QUESTIONS.length - 1) {
        setCurrentQ(q => q + 1);
        setSelectedOption(null);
        setAnswered(false);
      } else {
        setFinished(true);
      }
    }
  };
  if (finished) {
    return <div className="flex flex-col h-full" data-id="element-1373">
        <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2" data-id="element-1374">
          <div className="flex flex-col items-center text-center px-4 pt-4 mb-6" data-id="element-1375">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} data-id="element-1376">
              <CheckCircle2Icon className="w-16 h-16 text-green-500 mb-4" data-id="element-1377" />
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1378">
              Quiz Complete
            </h2>
            <div className="text-5xl font-black text-gray-900 mb-2 tracking-tighter" data-id="element-1379">
              {score}
              <span className="text-2xl text-gray-400" data-id="element-1380">
                {' '}
                / {QUIZ_QUESTIONS.length}
              </span>
            </div>
            <p className="text-gray-500 text-sm mb-6" data-id="element-1381">
              {score === QUIZ_QUESTIONS.length ? 'Perfect score. You have a strong understanding of the PTP.' : 'Review the missed items before starting work.'}
            </p>
            <div className="bg-white border border-gray-200 rounded-xl p-4 w-full max-w-sm" data-id="element-1382">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2" data-id="element-1383">
                Waiting for crew
              </p>
              <div className="space-y-2" data-id="element-1384">
                {[{
                name: 'Dan Kowalski',
                role: 'Crane Operator',
                status: 'completed'
              }, {
                name: 'Luis Reyes',
                role: 'Rigger',
                status: 'completed'
              }, {
                name: 'Jake Brennan',
                role: 'Ironworker',
                status: 'in progress'
              }, {
                name: 'Carlos Medina',
                role: 'Laborer',
                status: 'completed'
              }].map(w => <div key={w.name} className="flex items-center justify-between text-sm" data-id="element-1385">
                    <span className="text-gray-900" data-id="element-1386">
                      {w.name}{' '}
                      <span className="text-gray-400 text-xs" data-id="element-1387">· {w.role}</span>
                    </span>
                    {w.status === 'completed' ? <span className="text-green-600 text-xs font-medium" data-id="element-1388">
                        Completed
                      </span> : <span className="text-amber-600 text-xs font-medium" data-id="element-1389">
                        In progress...
                      </span>}
                  </div>)}
              </div>
            </div>
          </div>

          {/* PTP Acknowledgment */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 w-full" data-id="element-1390">
            <div className="flex items-center gap-2 mb-3" data-id="element-1391">
              <ShieldCheckIcon className="w-5 h-5 text-yellow-500" data-id="element-1392" />
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1393">
                PTP Acknowledgment
              </p>
            </div>
            <p className="text-gray-500 text-xs leading-relaxed mb-4" data-id="element-1394">
              By signing below, I acknowledge that I have reviewed and
              understand the Pre-Task Plan for this job, including all
              identified hazards, required controls, and safety procedures. I
              commit to following the PTP and will stop work immediately if
              conditions change or new hazards are identified.
            </p>
            <SignaturePad onSignatureChange={dataUrl => updateData({
            quizAcknowledgmentSignature: dataUrl || undefined
          })} data-id="element-1395" />
          </div>
        </div>

        <div className="sticky bottom-0 z-10 pt-3 pb-2 bg-gray-50" data-id="element-1396">
          <PrimaryButton onClick={onNext} disabled={!data.quizAcknowledgmentSignature} icon={<ShieldCheckIcon className="w-5 h-5" data-id="element-1398" />} data-id="element-1397">
            {data.quizAcknowledgmentSignature ? 'View Crew Results' : 'Sign to Continue'}
          </PrimaryButton>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-1399">
      <div className="mb-5" data-id="element-1400">
        <div className="flex items-center justify-between mb-2" data-id="element-1401">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-1402">PTP Safety Quiz</h2>
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider bg-white border border-gray-200 px-2.5 py-1 rounded-full" data-id="element-1403">
            {currentQ + 1} / {QUIZ_QUESTIONS.length}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5 flex gap-1" data-id="element-1404">
          {QUIZ_QUESTIONS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i < currentQ ? 'bg-green-500' : i === currentQ ? 'bg-yellow-500' : 'bg-gray-300'}`} data-id="element-1405" />)}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-1406">
        <AnimatePresence mode="wait" data-id="element-1407">
          <motion.div key={currentQ} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.25
        }} data-id="element-1408">
            <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4" data-id="element-1409">
              <p className="text-gray-900 font-semibold text-base leading-relaxed" data-id="element-1410">
                {question.question}
              </p>
            </div>
            <div className="space-y-2.5" data-id="element-1411">
              {question.options.map((option, i) => {
              let borderClass = 'border-gray-200';
              let bgClass = 'bg-white';
              let textClass = 'text-gray-700';
              let iconEl = null;
              if (answered) {
                if (i === question.correctIndex) {
                  borderClass = 'border-green-300';
                  bgClass = 'bg-green-50';
                  textClass = 'text-green-700';
                  iconEl = <CheckCircle2Icon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1412" />;
                } else if (i === selectedOption && i !== question.correctIndex) {
                  borderClass = 'border-red-300';
                  bgClass = 'bg-red-50';
                  textClass = 'text-red-600';
                  iconEl = <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-1413" />;
                }
              } else if (i === selectedOption) {
                borderClass = 'border-yellow-300';
                bgClass = 'bg-yellow-50';
                textClass = 'text-yellow-700';
              }
              const letter = String.fromCharCode(65 + i);
              return <button key={i} onClick={() => handleSelect(i)} disabled={answered} className={`w-full ${bgClass} border ${borderClass} rounded-xl px-4 py-3.5 flex items-center gap-3 text-left transition-colors`} data-id="element-1414">
                    <span className={`w-7 h-7 rounded-full border ${i === selectedOption && !answered ? 'border-yellow-500 bg-yellow-100 text-yellow-600' : answered && i === question.correctIndex ? 'border-green-500 bg-green-100 text-green-600' : answered && i === selectedOption ? 'border-red-500 bg-red-100 text-red-500' : 'border-gray-300 text-gray-400'} flex items-center justify-center text-xs font-bold flex-shrink-0`} data-id="element-1415">
                      {letter}
                    </span>
                    <span className={`${textClass} text-sm font-medium flex-1`} data-id="element-1416">
                      {option}
                    </span>
                    {iconEl}
                  </button>;
            })}
            </div>
            <AnimatePresence data-id="element-1417">
              {answered && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25
            }} className={`mt-4 rounded-xl p-4 ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-id="element-1418">
                  <p className={`text-sm font-semibold ${isCorrect ? 'text-green-600' : 'text-red-500'}`} data-id="element-1419">
                    {isCorrect ? 'Correct!' : 'Incorrect'}
                  </p>
                  {!isCorrect && <p className="text-gray-700 text-xs mt-1" data-id="element-1420">
                      The correct answer is:{' '}
                      <span className="text-gray-900 font-medium" data-id="element-1421">
                        {question.options[question.correctIndex]}
                      </span>
                    </p>}
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 bg-gray-50" data-id="element-1422">
        <PrimaryButton onClick={handleConfirm} disabled={selectedOption === null} icon={answered ? <ChevronRightIcon className="w-5 h-5" data-id="element-1424" /> : undefined} data-id="element-1423">
          {!answered ? 'Confirm Answer' : currentQ < QUIZ_QUESTIONS.length - 1 ? 'Next Question' : 'Finish Quiz'}
        </PrimaryButton>
      </div>
    </div>;
}